import React from "react";
import type {Bid, Lot} from "@nextlot/core/webcast/data/data_types_definitions";
import {RoundStopTypeEnum, RoundTypeEnum} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {connect} from "react-redux";
import AuctionsManager from "@nextlot/core/webcast/services/AuctionsManager";
import {CurrencyAmountDisplay} from "../CurrencyAmountDisplay";
import {useTranslation} from "react-i18next";
import {ReduxWebcastCurrentRoundType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRound";
import {ReduxWebcastCurrentRoundBiddingType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRoundBidding";
import {ReduxActiveWebcastSubStateType} from "@nextlot/core/webcast/redux/reducerActiveWebcast";


type RootCmpPropsType = {
    currentRound: ReduxWebcastCurrentRoundType,
    currentRoundBidding: ReduxWebcastCurrentRoundBiddingType,
    lotsMapById: Map<number, Lot>,
}

function renderWinningBid(currentRound, lotsMapById, winningBid: Bid, t) {
    const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;
    const lots: Array<Lot> = currentRound.lotsIds.map((lotId) => lotsMapById.get(lotId));
    let totalBid;

    if (currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT) {
        if (lots[0].itemsQty > 1) {
            totalBid = winningBid.value * lots[0].itemsQty;
        }
    }

    return (
        <div>
            {
                winningBid.userId === myUserId
                    ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
                    ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.highestBidder')}</h3>
                    : <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.youWon')} {lots.length > 1 ? `${t('common.lots')}`: `${t('common.lot')}`}: {lots.map((lot: Lot) => `#${lot.number}`).join(", ")}</h3>)
                    : (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
                    ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.waitingWinner')}</h3>
                    :
                    <h3 className="py-1 px-3 bg-danger text-white">{t('biddingArea.sold')}</h3>)
            }

            {(
                currentRound.roundType === RoundTypeEnum.SINGLE_NORMAL ||
                currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT ||
                currentRound.roundType === RoundTypeEnum.GROUP_ONEMONEY ||
                currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
            )
            &&
            <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
                valueCents={winningBid.value}/> {currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ? `${t('common.each')}` : ''}
            </h4>
            }

            {
                (currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT && totalBid) &&
                <h4>{t('biddingArea.totalPrice')}: <CurrencyAmountDisplay valueCents={totalBid}/></h4>
            }
        </div>
    )
}

function renderPendingBid(currentRound, highestBid: Bid, t) {
    const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;

    return (
        <div>
            <h3 className="py-1 px-3 mb-3 bg-warning text-dark">
                {
                    highestBid.userId === myUserId
                        ? `${t('biddingArea.yourBidPending')}`
                        : (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
                            ? `${t('biddingArea.waitingWinnerChooseLots')}`
                            : `${t('biddingArea.pendingSellerConfirmation')}`
                        )
                }
            </h3>
            {(
                currentRound.roundType === RoundTypeEnum.SINGLE_NORMAL ||
                currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT ||
                currentRound.roundType === RoundTypeEnum.GROUP_ONEMONEY ||
                currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
            )
            &&
            <h4>{t('biddingArea.bid')}: <CurrencyAmountDisplay
                valueCents={highestBid.value}/> {currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ? `${t('common.each')}` : ''}
            </h4>
            }
        </div>
    )
}

function renderContent(props: RootCmpPropsType, t) {
    const {currentRound, currentRoundBidding, lotsMapById} = props;
    const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;


    if ((currentRound.stopType === RoundStopTypeEnum.SOLD || currentRound.stopType === RoundStopTypeEnum.PENDING) &&
        (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN) &&  // over-kill to check roundType too, but for consistency clarity
        (currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)) {

        const myLotsSelection = currentRoundBidding.choiceLotsSelection
            .filter(pair => (pair[1] === myUserId))
            .map(pair => lotsMapById.get(pair[0]))
            .sort((lot1: Lot, lot2: Lot) => (lot1.orderIdx - lot2.orderIdx));


        return (
            <React.Fragment>
                {
                    myLotsSelection.length > 0
                        ? <div>
                            <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.auctioneerConfirmed')} {myLotsSelection.length > 1 ? `${t('common.lots')}`: `${t('common.lot')}`}:
                                <br/>
                                {myLotsSelection.map((lot: Lot) => `#${lot.number}`).join(", ")}</h3>
                            {props.currentRound.stopType === RoundStopTypeEnum.SOLD
                                ? (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
                                    ? <h4>{t('biddingArea.soldPriceForEach')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/></h4>
                                    : <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/> {t('common.each')}</h4>)
                                : (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
                                    ? <h4>{t('biddingArea.pendingSellerConfirmationForEach')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/></h4>
                                    : <h4>{t('biddingArea.pendingBid')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/></h4>)
                            }
                        </div>
                        :
                        props.currentRound.stopType === RoundStopTypeEnum.SOLD
                            ? (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
                                ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.auctioneerRefused')}</h3>
                                :
                                <div>
                                    <h3 className="py-1 px-3 bg-danger text-white">{t('biddingArea.sold')}</h3>
                                    <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/></h4>
                                </div>
                            )
                            : (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
                                ? <h3 className="py-1 px-3 bg-success text-white">
                                    {t('biddingArea.auctioneerRefused')}
                                </h3>
                                :
                                <div>
                                    <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.pendingSellerConfirmation')}</h3>
                                    <h4>{t('biddingArea.bid')}: <CurrencyAmountDisplay
                                        valueCents={props.currentRoundBidding.highestBid.value}/></h4>
                                </div>
                            )

                }
            </React.Fragment>
        );

    } else {
        switch (currentRound.stopType) {
            case RoundStopTypeEnum.CANCEL:
                return <h3 className="py-1 px-3 bg-dark text-white">{t('biddingArea.roundCancelled')}</h3>;

            case RoundStopTypeEnum.NO_SALE:
                return <h3 className="py-1 px-3 bg-danger text-white">{t('biddingArea.noSale')}</h3>;

            case RoundStopTypeEnum.SOLD:
                return (
                    <React.Fragment>
                        {renderWinningBid(currentRound, lotsMapById, currentRoundBidding.highestBid, t)}
                    </React.Fragment>
                );

            case RoundStopTypeEnum.PENDING:
                return (
                    <React.Fragment>
                        {renderPendingBid(currentRound, currentRoundBidding.highestBid, t)}
                    </React.Fragment>
                );
            default:
                if (process.env.NODE_ENV === "development") {
                    return <h1>!!! INVALID Round.stopType: {currentRound.stopType}</h1>
                }
                return null;
        }
    }
}

function BidderRoundStopInfoCmp(props: RootCmpPropsType) {

    const { t, i18n } = useTranslation();

    return (
        <div className="p-3 d-flex flex-column flex-grow-1">
            {renderContent(props, t)}
        </div>
    )
}

export default connect(({activeWebcast}: { activeWebcast: ReduxActiveWebcastSubStateType }) => {
    return {
        currentRound: activeWebcast.webcastCurrentRound,
        currentRoundBidding: activeWebcast.webcastCurrentRoundBidding,
        lotsMapById: activeWebcast.webcastAuctionData.lotsMapById,
    };
})(BidderRoundStopInfoCmp);




// type RootCmpPropsType = {
//     currentRound: ReduxWebcastCurrentRoundType,
//     currentRoundBidding: ReduxWebcastCurrentRoundBiddingType,
//     lotsMapById: Map,
// }
//
//
// function renderWinningBid(currentRound, lotsMapById, winningBid: Bid, t) {
//     const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;
//     const lots: Array<Lot> = currentRound.lotsIds.map((lotId) => lotsMapById.get(lotId));
//     let totalBid = '';
//
//     if (currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT) {
//         if (lots[0].itemsQty > 1) {
//             totalBid = winningBid.value * lots[0].itemsQty;
//         }
//     }
//
//     return (
//         <div>
//             {
//                 winningBid.userId === myUserId
//                     ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
//                     ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.highestBidder')}</h3>
//                     : <h3 className="py-1 px-3 bg-success text-white">
//                         {t('biddingArea.youWon')}
//                         {lots.length > 1 ? `${t('common.lots')}`: `${t('common.lot')}`}: {lots.map((lot: Lot) => `#${lot.number}`).join(", ")}</h3>)
//                     : (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
//                     ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.waitingWinner')}</h3>
//                     :
//                     <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.sold')}</h3>)
//             }
//
//             {(
//                 currentRound.roundType === RoundTypeEnum.SINGLE_NORMAL ||
//                 currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT ||
//                 currentRound.roundType === RoundTypeEnum.GROUP_ONEMONEY ||
//                 currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
//             )
//             &&
//             <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
//                 valueCents={winningBid.value}/> {currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ? `${t('common.each')}` : ''}
//             </h4>
//             }
//
//             {
//                 (currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT && totalBid) &&
//                 <h4>{t('biddingArea.totalPrice')}: <CurrencyAmountDisplay valueCents={totalBid}/></h4>
//             }
//         </div>
//     )
// }
//
// function renderPendingBid(currentRound, highestBid: Bid, t) {
//     const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;
//
//     return (
//         <div>
//             <h3 className="py-1 px-3 mb-3 bg-warning text-dark">
//                 {
//                     highestBid.userId === myUserId
//                         ? `${t('biddingArea.yourBidPending')}`
//                         : (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE
//                             ? `${t('biddingArea.waitingWinnerChooseLots')}`
//                             : `${t('biddingArea.pendingSellerConfirmation')}`
//                         )
//                 }
//             </h3>
//             {(
//                 currentRound.roundType === RoundTypeEnum.SINGLE_NORMAL ||
//                 currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT ||
//                 currentRound.roundType === RoundTypeEnum.GROUP_ONEMONEY ||
//                 currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
//             )
//             &&
//             <h4>{t('biddingArea.bid')}: <CurrencyAmountDisplay
//                 valueCents={highestBid.value}/> {currentRound.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ? `${t('common.each')}` : ''}
//             </h4>
//             }
//         </div>
//     )
// }
//
//
// function renderContent(props: RootCmpPropsType, t) {
//     const {currentRound, currentRoundBidding, lotsMapById} = props;
//     const myUserId: number = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;
//
//
//     if ((currentRound.stopType === RoundStopTypeEnum.SOLD || currentRound.stopType === RoundStopTypeEnum.PENDING) &&
//         (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN) &&  // over-kill to check roundType too, but for consistency clarity
//         (currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)) {
//
//         const myLotsSelection = currentRoundBidding.choiceLotsSelection
//             .filter(pair => (pair[1] === myUserId))
//             .map(pair => lotsMapById.get(pair[0]))
//             .sort((lot1: Lot, lot2: Lot) => (lot1.orderIdx - lot2.orderIdx));
//
//
//         return (
//             <React.Fragment>
//                 {
//                     myLotsSelection.length > 0
//                         ? <div>
//                             <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.auctioneerConfirmed')} {myLotsSelection.length > 1 ? `${t('common.lots')}`: `${t('common.lot')}`}:
//                                 <br/>
//                                 {myLotsSelection.map((lot: Lot) => `#${lot.number}`).join(", ")}</h3>
//                             {props.currentRound.stopType === RoundStopTypeEnum.SOLD
//                                 ? (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
//                                     ? <h4>{t('biddingArea.soldPriceForEach')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/></h4>
//                                     : <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/> {t('common.each')}</h4>)
//                                 : (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
//                                     ? <h4>{t('biddingArea.pendingSellerConfirmationForEach')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/></h4>
//                                     : <h4>{t('biddingArea.pendingBid')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/></h4>)
//                             }
//                         </div>
//                         :
//                         props.currentRound.stopType === RoundStopTypeEnum.SOLD
//                             ? (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
//                                 ? <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.auctioneerRefused')}</h3>
//                                 :
//                                 <div>
//                                     <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.sold')}</h3>
//                                     <h4>{t('biddingArea.winningBid')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/></h4>
//                                 </div>
//                             )
//                             : (props.currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN
//                                 ? <h3 className="py-1 px-3 bg-success text-white">
//                                     {t('biddingArea.auctioneerRefused')}
//                                 </h3>
//                                 :
//                                 <div>
//                                     <h3 className="py-1 px-3 bg-success text-white">{t('biddingArea.pendingSellerConfirmation')}</h3>
//                                     <h4>{t('biddingArea.bid')}: <CurrencyAmountDisplay
//                                         valueCents={props.currentRoundBidding.highestBid.value}/></h4>
//                                 </div>
//                             )
//
//                 }
//             </React.Fragment>
//         );
//
//     } else {
//         switch (currentRound.stopType) {
//             case RoundStopTypeEnum.CANCEL:
//                 return <h3 className="py-1 px-3 bg-dark text-white">{t('biddingArea.roundCancelled')}</h3>;
//
//             case RoundStopTypeEnum.NO_SALE:
//                 return <h3 className="py-1 px-3 bg-danger text-white">{t('biddingArea.noSale')}</h3>;
//
//             case RoundStopTypeEnum.SOLD:
//                 return (
//                     <React.Fragment>
//                         {renderWinningBid(currentRound, lotsMapById, currentRoundBidding.highestBid, t)}
//                     </React.Fragment>
//                 );
//
//             case RoundStopTypeEnum.PENDING:
//                 return (
//                     <React.Fragment>
//                         {renderPendingBid(currentRound, currentRoundBidding.highestBid, t)}
//                     </React.Fragment>
//                 );
//             default:
//                 if (process.env.NODE_ENV === "development") {
//                     return <h1>!!! INVALID Round.stopType: {currentRound.stopType}</h1>
//                 }
//                 return null;
//         }
//     }
// }
//
// const BidderRoundStopInfoCmp = (props: RootCmpPropsType) => {
//     const {t} = useTranslation();
//
//     return (
//         <div className="p-3 d-flex flex-column flex-grow-1">
//             {renderContent(props, t)}
//         </div>
//     )
// };
//
// export default connect(({activeWebcast}: { activeWebcast: ReduxActiveWebcastSubStateType }) => {
//     return {
//         currentRound: activeWebcast.webcastCurrentRound,
//         currentRoundBidding: activeWebcast.webcastCurrentRoundBidding,
//         lotsMapById: activeWebcast.webcastAuctionData.lotsMapById,
//     };
// })(BidderRoundStopInfoCmp);

